import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

export const useRecipes = ({
  activeApi,
  token,
  ingredients,
  addIngredient,
  selectedCookbookIds,
  selectedCategoryIds,
  selectedIngredientIds,
  onRecipeSaved,
  onRecipeDeleted,
  setConfirmationModal,
}) => {
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [recipePage, setRecipePage] = useState(1);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    if (token) {
      if (!hasFetchedData.current) {
        hasFetchedData.current = true;
        activeApi.getRecipes().then(setRecipes).catch(error => console.error("Error fetching recipes:", error));
      }
    } else {
      hasFetchedData.current = false;
    }
  }, [token, activeApi]);

  const addRecipe = useCallback(async (recipe, currentEditingRecipeId, andAnother) => {
    const existingRecipe = currentEditingRecipeId
      ? recipes.find(r => r.name.toLowerCase() === recipe.name.toLowerCase() && r.id !== currentEditingRecipeId)
      : recipes.find(r => r.name.toLowerCase() === recipe.name.toLowerCase());

    if (existingRecipe) {
      return { error: 'A recipe with this name already exists.' };
    }

    try {
      // Reconcile ingredients before saving the recipe
      const reconciledIngredients = await Promise.all(recipe.ingredients.map(async (ing) => {
        if (typeof ing.id === 'string' && ing.id.startsWith('temp-')) {
          // This is a new ingredient, check if it already exists by name
          const existingIngredient = ingredients.find(i => i.name.toLowerCase() === ing.name.toLowerCase());
          if (existingIngredient) {
            // Ingredient already exists, use its ID
            return { ...ing, id: existingIngredient.id, name: existingIngredient.name };
          } else {
            // This is a new ingredient, save it first
            const result = await addIngredient({ name: ing.name });
            if (result.error) {
              throw new Error(`Failed to add new ingredient "${ing.name}": ${result.error}`);
            }
            // Return a new ingredient object for the recipe with the permanent ID
            return { ...ing, id: result.ingredient.id, name: result.ingredient.name };
          }
        }
        // This is an existing ingredient, just return it
        return ing;
      }));

      const recipeToSave = { ...recipe, ingredients: reconciledIngredients };

      const savedRecipe = currentEditingRecipeId
        ? await activeApi.updateRecipeAPI(currentEditingRecipeId, recipeToSave)
        : await activeApi.addRecipeAPI(recipeToSave);

      if (currentEditingRecipeId) {
        setRecipes(prev => prev.map(r => (r.id === currentEditingRecipeId ? savedRecipe : r)));
        setEditingRecipeId(null);
      } else {
        setRecipes(prev => [...prev, savedRecipe]);
      }

      onRecipeSaved(savedRecipe, currentEditingRecipeId);

      if (!andAnother) {
        setIsRecipeModalOpen(false);
      }
      return { success: true };
    } catch (error) {
      console.error("Failed to save recipe:", error);
      return { error: error.message };
    }
  }, [recipes, activeApi, onRecipeSaved, addIngredient]);

  const deleteRecipe = useCallback((recipeId) => {
    const recipeToDelete = recipes.find(r => r.id === recipeId);
    const message = recipeToDelete
      ? `Are you sure you want to delete recipe '${recipeToDelete.name}'?`
      : 'Are you sure you want to delete this recipe?';

    setConfirmationModal({
      isOpen: true,
      message,
      onConfirm: () => {
        activeApi.deleteRecipeAPI(recipeId)
          .then(() => {
            const newRecipes = recipes.filter(recipe => recipe.id !== recipeId);
            setRecipes(newRecipes);
            onRecipeDeleted(recipeId);

            const totalPages = Math.ceil(newRecipes.length / 5);
            if (recipePage > totalPages) {
              setRecipePage(totalPages || 1);
            }
          })
          .catch(error => console.error('Error deleting recipe:', error))
          .finally(() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { } }));
      }
    });
  }, [activeApi, recipes, recipePage, setConfirmationModal, onRecipeDeleted]);

  const startEditingRecipe = useCallback((recipe) => {
    setEditingRecipeId(recipe.id);
    setIsRecipeModalOpen(true);
  }, []);

  const openAddRecipeModal = useCallback(() => {
    setEditingRecipeId(null);
    setIsRecipeModalOpen(true);
  }, []);

  const cancelEditingRecipe = useCallback(() => {
    setEditingRecipeId(null);
    setIsRecipeModalOpen(false);
  }, []);

  const handleRecipeSearchChange = useCallback((term) => {
    setSearchTerm(term);
    setRecipePage(1);
  }, []);

  const selectedIngredientNames = useMemo(() =>
    selectedIngredientIds.map(id => ingredients.find(i => i.id === id)?.name.toLowerCase()).filter(Boolean),
    [selectedIngredientIds, ingredients]
  );

  const filteredRecipes = useMemo(() => recipes.filter(recipe => {
    const searchMatch = recipe.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Cookbook Logic
    let cookbookMatch = true;
    if (selectedCookbookIds.length > 0) {
      const filters = selectedCookbookIds.map(f => (typeof f === 'string' ? { id: f, operation: 'OR' } : f));
      const notFilters = filters.filter(f => f.operation === 'NOT');
      const orFilters = filters.filter(f => f.operation === 'OR');

      if (notFilters.some(f => f.id === recipe.cookbookId)) {
        cookbookMatch = false;
      }

      if (cookbookMatch && orFilters.length > 0) {
        if (!orFilters.some(f => f.id === recipe.cookbookId)) {
          cookbookMatch = false;
        }
      }
    }

    // Advanced Category Logic
    let categoryMatch = true;
    if (selectedCategoryIds.length > 0) {
      const recipeCategoryIds = recipe.categoryIds || [];

      // Group filters by operation
      const andFilters = selectedCategoryIds.filter(f => f.operation === 'AND');
      const orFilters = selectedCategoryIds.filter(f => f.operation === 'OR');
      const notFilters = selectedCategoryIds.filter(f => f.operation === 'NOT');

      // Check NOT filters (Recipe must NOT have ANY of these)
      if (notFilters.some(f => recipeCategoryIds.includes(f.id))) {
        categoryMatch = false;
      }

      // Check AND filters (Recipe must have ALL of these)
      if (categoryMatch && andFilters.length > 0) {
        if (!andFilters.every(f => recipeCategoryIds.includes(f.id))) {
          categoryMatch = false;
        }
      }

      // Check OR filters (Recipe must have AT LEAST ONE of these, if any ORs exist)
      if (categoryMatch && orFilters.length > 0) {
        if (!orFilters.some(f => recipeCategoryIds.includes(f.id))) {
          categoryMatch = false;
        }
      }
    }

    // Advanced Ingredient Logic
    if (categoryMatch && selectedIngredientIds.length > 0) {
      if (!recipe.ingredients) return false;

      // Normalize recipe ingredients to lower case names for comparison if IDs are missing
      const recipeIngredientIds = recipe.ingredients.map(i => i.id);
      const recipeIngredientNames = recipe.ingredients.map(i => i.name.toLowerCase());

      const ingredientIsPresent = (filterId) => {
        // Find the filter object to get the ID (or we already have it)
        // Check by ID first
        if (recipeIngredientIds.includes(filterId)) return true;

        // Fallback to name match if needed (e.g. if recipe data is potentially old or from different source)
        // We need the name of the ingredient from the master list to compare
        const filterIngredient = ingredients.find(i => i.id === filterId);
        if (filterIngredient && recipeIngredientNames.includes(filterIngredient.name.toLowerCase())) return true;

        return false;
      }

      // Group filters by operation
      const andIngs = selectedIngredientIds.filter(f => f.operation === 'AND');
      const orIngs = selectedIngredientIds.filter(f => f.operation === 'OR');
      const notIngs = selectedIngredientIds.filter(f => f.operation === 'NOT');

      // Check NOT filters
      if (notIngs.some(f => ingredientIsPresent(f.id))) {
        return false;
      }

      // Check AND filters
      if (andIngs.length > 0) {
        if (!andIngs.every(f => ingredientIsPresent(f.id))) {
          return false;
        }
      }

      // Check OR filters
      if (orIngs.length > 0) {
        if (!orIngs.some(f => ingredientIsPresent(f.id))) {
          return false;
        }
      }
    }

    return searchMatch && cookbookMatch && categoryMatch;
  }), [searchTerm, recipes, selectedCookbookIds, selectedCategoryIds, selectedIngredientIds, ingredients]);

  return {
    recipes,
    setRecipes,
    filteredRecipes,
    searchTerm,
    handleRecipeSearchChange,
    recipePage,
    setRecipePage,
    editingRecipeId,
    isRecipeModalOpen,
    setIsRecipeModalOpen,
    addRecipe,
    deleteRecipe,
    startEditingRecipe,
    openAddRecipeModal,
    cancelEditingRecipe,
  };
};